import React, { useMemo, useState } from 'react';
import styles from './ListingNoteCombobox.module.css';
import { splitListingNotes, toggleListingNote } from './seatLevelNotes';

type ListingNoteComboboxProps = {
  value: string;
  options: string[];
  sentValue?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
};

const ListingNoteCombobox: React.FC<ListingNoteComboboxProps> = ({
  value,
  options,
  sentValue = '',
  disabled = false,
  placeholder = 'Search or enter a note',
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const split = useMemo(() => splitListingNotes(value, options), [value, options]);
  const filter = split.base.replace(/^CUSTOM:\d+(?:,\d+)*$/i, '').trim().toLowerCase();
  const selectedKeys = useMemo(
    () => new Set(split.selected.map((option) => option.toLowerCase())),
    [split.selected],
  );
  const visibleOptions = useMemo(
    () => options.filter((option) => {
      const key = option.trim().toLowerCase();
      return selectedKeys.has(key) || !filter || key.includes(filter);
    }),
    [options, filter, selectedKeys],
  );

  const toggle = (option: string) => {
    onChange(toggleListingNote(value, options, option));
    setOpen(true);
    setActiveIndex(0);
  };

  return (
    <div className={styles.wrap}>
      <input
        className={styles.input}
        data-listing-note-input=""
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) => Math.min(index + 1, Math.max(visibleOptions.length - 1, 0)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && open && visibleOptions[activeIndex]) {
            event.preventDefault();
            toggle(visibleOptions[activeIndex]);
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && !disabled && (
        <ul className={styles.menu} role="listbox" aria-multiselectable="true">
          {visibleOptions.length ? visibleOptions.map((option, index) => {
            const checked = selectedKeys.has(option.trim().toLowerCase());
            return (
              <li key={option} role="option" aria-selected={checked}>
                <label
                  className={`${styles.option} ${index === activeIndex ? styles.optionActive : ''}`}
                  data-listing-note-option=""
                  onMouseDown={(event) => {
                    event.preventDefault();
                    toggle(option);
                  }}
                >
                  <input type="checkbox" checked={checked} readOnly tabIndex={-1} />
                  <span className={styles.optionText}>{option}</span>
                </label>
              </li>
            );
          }) : (
            <li className={styles.empty}>No matching notes. The text you enter will be used.</li>
          )}
        </ul>
      )}
      {sentValue.trim() && sentValue.trim() !== value.trim() ? (
        <p className={styles.preview}>{sentValue}</p>
      ) : null}
    </div>
  );
};

export default ListingNoteCombobox;
